import { formatRecordDate } from "@taunggyi-plus/design-system";
import { ActionIcon } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { AdPackages, type AdPackage } from "./ad-packages";
import { FeeReceiptDialog, receiptLabels, receiptStatus } from "./payment-receipt";
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import { useAuth } from "@taunggyi-plus/auth";
import {
  Card,
  DetailCard,
  RECORDS_PER_PAGE,
  ServerPagination,
  ManagementListToolbar,
  PaginatedList,
  StatusPill,
} from "@taunggyi-plus/design-system";

type FinanceRole = "admin" | "customer" | "hotel" | "merchant" | "delivery" | "taxi";
type Direction = "income" | "expense";
type ReportPeriod = "30" | "90" | "365" | "all";
interface Organization {
  id: string;
  name: string;
  kind?: string;
}
interface Entry {
  id: string;
  organization_id: string | null;
  customer_user_id: string | null;
  direction: Direction;
  category: string;
  amount_minor: number;
  currency: string;
  description: string;
  occurred_at: string;
  received_minor: number;
  receipts: { amount_minor: number; received_on: string; reference: string }[];
  organizations: { name: string } | Array<{ name: string }> | null;
}
interface Subscription {
  id: string;
  organization_id: string;
  plan: "starter" | "growth" | "enterprise";
  status: "trial" | "active" | "past_due" | "suspended" | "cancelled";
  billing_cycle: "monthly" | "annual";
  fee_minor: number;
  currency: string;
  starts_at: string;
  renews_at: string | null;
  updated_at: string;
}

const labels: Record<string, string> = {
  service_sale: "Service sale",
  booking_value: "Booking value",
  cash_collection: "Cash collected",
  subscription_fee: "Subscription fee",
  event_fee: "Event participation fee",
  promotion_fee: "Special promotion fee",
  refund: "Refund",
  manual_income: "Manual income",
  manual_expense: "Manual expense",
};
const money = (minor: number, currency = "MMK") =>
  `${(minor / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
const orgName = (entry: Entry) =>
  Array.isArray(entry.organizations)
    ? (entry.organizations[0]?.name ?? "Customer account")
    : (entry.organizations?.name ?? "Customer account");
const entryDirection = (entry: Entry, role: FinanceRole): Direction =>
  role !== "admin" &&
  role !== "customer" &&
  (entry.category === "subscription_fee" ||
    entry.category === "promotion_fee" ||
    entry.category === "event_fee")
    ? "expense"
    : entry.direction;

export function FinanceOverview({ role }: { readonly role: FinanceRole }) {
  const auth = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [query, setQuery] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("all");
  const [scope, setScope] = useState(role === "admin" ? "platform" : "all");
  const [receiptEntry, setReceiptEntry] = useState<Entry | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [direction, setDirection] = useState<"all" | Direction>("all");
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>("30");
  const [recordOpen, setRecordOpen] = useState(false);
  const reportFilterDialog = useRef<HTMLDialogElement>(null);
  const [draftReport, setDraftReport] = useState({ scope, reportPeriod, direction, paymentStatus });
  const [refreshing, setRefreshing] = useState(false);
  const [reportAsOf, setReportAsOf] = useState(() => Date.now());
  const [message, setMessage] = useFeedback();
  const [busy, setBusy] = useState(false);
  const [defaultOccurredAt] = useState(() =>
    new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
  );

  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const [ledger, orgs, subscriptions] = await Promise.all([
      auth.client.rpc("finance_records"),
      role === "customer"
        ? Promise.resolve({ data: [], error: null })
        : auth.client.from("organizations").select("id,name,kind").order("name"),
      role === "admin" || role === "customer"
        ? Promise.resolve({ data: [], error: null })
        : auth.client
            .from("organization_subscriptions")
            .select(
              "id,organization_id,plan,status,billing_cycle,fee_minor,currency,starts_at,renews_at,updated_at",
            )
            .limit(1),
    ]);
    const error = ledger.error ?? orgs.error ?? subscriptions.error;
    if (error) {
      setMessage(error.message);
      return;
    }
    const organizationRows = (orgs.data as Organization[] | null) ?? [];
    const ledgerRows = ((ledger.data as Omit<Entry, "organizations">[] | null) ?? []).map(
      (entry) => ({
        ...entry,
        organizations: entry.organization_id
          ? {
              name:
                organizationRows.find((item) => item.id === entry.organization_id)?.name ??
                "Partner",
            }
          : null,
      }),
    );
    setEntries(ledgerRows);
    setLoaded(true);
    setReportAsOf(Date.now());
    setOrganizations(organizationRows);
    setSubscription(((subscriptions.data as Subscription[] | null) ?? [])[0] ?? null);
  }, [auth.client, auth.user, role, setMessage]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  useEffect(() => {
    if (!recordOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setRecordOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [recordOpen]);

  const scopedEntries = useMemo(
    () =>
      entries.filter(
        (entry) =>
          (role === "customer" || entry.organization_id !== null) &&
          (scope === "all" ||
            (scope === "platform") ===
              ["subscription_fee", "promotion_fee", "event_fee"].includes(entry.category)),
      ),
    [entries, role, scope],
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const threshold = reportPeriod === "all" ? 0 : reportAsOf - Number(reportPeriod) * 86400000;
    return scopedEntries.filter(
      (entry) =>
        new Date(entry.occurred_at).getTime() >= threshold &&
        new Date(entry.occurred_at).getTime() <= reportAsOf &&
        (direction === "all" || entryDirection(entry, role) === direction) &&
        (paymentStatus === "all" || receiptStatus(entry) === paymentStatus) &&
        (!needle ||
          `${entry.description} ${orgName(entry)} ${labels[entry.category] ?? entry.category}`
            .toLowerCase()
            .includes(needle)),
    );
  }, [direction, scopedEntries, query, role, paymentStatus, reportPeriod, reportAsOf]);
  const income = entries
    .filter((entry) => entryDirection(entry, role) === "income")
    .reduce((n, e) => n + e.amount_minor, 0);
  const expenses = entries
    .filter((entry) => entryDirection(entry, role) === "expense")
    .reduce((n, e) => n + e.amount_minor, 0);
  const reportEntries = filtered;
  const reportIncome = reportEntries
    .filter(
      (entry) =>
        entryDirection(entry, role) === "income" &&
        !["booking_value", "cash_collection"].includes(entry.category),
    )
    .reduce((total, entry) => total + entry.amount_minor, 0);
  const reportExpenses = reportEntries
    .filter((entry) => entryDirection(entry, role) === "expense")
    .reduce((total, entry) => total + entry.amount_minor, 0);
  const categoryReport = useMemo(() => {
    const totals = new Map<string, { income: number; expenses: number; count: number }>();
    for (const entry of reportEntries) {
      const current = totals.get(entry.category) ?? { income: 0, expenses: 0, count: 0 };
      if (entryDirection(entry, role) === "income") current.income += entry.amount_minor;
      else current.expenses += entry.amount_minor;
      current.count += 1;
      totals.set(entry.category, current);
    }
    return Array.from(totals, ([category, values]) => ({ category, ...values })).sort(
      (a, b) => b.income + b.expenses - (a.income + a.expenses),
    );
  }, [reportEntries, role]);
  const largestCategory = Math.max(
    1,
    ...categoryReport.map((category) => category.income + category.expenses),
  );

  async function record(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy(true);
    setMessage("");
    const { error } = await auth.client.rpc("record_finance_entry", {
      target_organization_id: String(form.get("organization_id")),
      target_direction: String(form.get("direction")),
      target_amount_minor: Math.round(Number(form.get("amount")) * 100),
      target_description: String(form.get("description")),
      target_occurred_at: new Date(String(form.get("occurred_at"))).toISOString(),
    });
    setBusy(false);
    if (error) {
      setMessage(error.message);
      return;
    }
    formElement.reset();
    setRecordOpen(false);
    setScope(role === "admin" ? "provider" : "all");
    setQuery("");
    setDirection("all");
    setPaymentStatus("all");
    setReportPeriod("all");
    setMessage("Finance record added. The audit trail was updated.");
    await load();
  }

  if (auth.user && !loaded)
    return (
      <Card>
        <p role="status">{message || "Loading finance records…"}</p>
        {message && (
          <button type="button" className="button" onClick={() => void load()}>
            Retry
          </button>
        )}
      </Card>
    );
  if (!auth.user)
    return (
      <Card className="empty-state">
        <strong>Sign in to view finance.</strong>
      </Card>
    );
  return (
    <section className="finance-overview">
      {!loaded && <p role="status">{message || "Loading finance records…"}</p>}
      {receiptEntry && (
        <FeeReceiptDialog
          entry={receiptEntry}
          onClose={() => setReceiptEntry(null)}
          onSaved={() => void load()}
        />
      )}

      {subscription && (
        <Card className="subscription-summary">
          <div>
            <p className="eyebrow">Owner subscription</p>
            <h2>{subscription.plan} plan</h2>
            <p>
              {money(subscription.fee_minor)} / {subscription.billing_cycle}
            </p>
          </div>
          <StatusPill tone={subscription.status === "active" ? "success" : "warning"}>
            {subscription.status.replace("_", " ")}
          </StatusPill>
        </Card>
      )}
      {role === "customer" ? (
        <div className="operations-grid finance-grid">
          <Card className="metric-card">
            <span>Recorded spending</span>
            <strong>{money(expenses)}</strong>
          </Card>
          <Card className="metric-card">
            <span>Refunds / credits</span>
            <strong>{money(income)}</strong>
          </Card>
          <Card className="metric-card">
            <span>Ledger records</span>
            <strong>{entries.length}</strong>
          </Card>
        </div>
      ) : (
        <section className="finance-report" aria-labelledby="finance-report-title">
          <div className="finance-report__heading">
            <div>
              <p className="eyebrow">Finance report</p>
              <h2 id="finance-report-title">Recorded finances</h2>
            </div>
            <div className="finance-report__actions">
              {organizations.length > 0 && (
                <button
                  className="toolbar-icon-button"
                  type="button"
                  aria-label="Add finance record"
                  title="Add finance record"
                  onClick={() => setRecordOpen(true)}
                >
                  <ActionIcon action="add" />
                </button>
              )}
              <button
                className="toolbar-icon-button"
                type="button"
                aria-label="Filter finance"
                title="Filter finance"
                aria-haspopup="dialog"
                data-filtered={
                  scope !== (role === "admin" ? "platform" : "all") ||
                  reportPeriod !== "30" ||
                  direction !== "all" ||
                  paymentStatus !== "all"
                }
                onClick={() => {
                  setDraftReport({ scope, reportPeriod, direction, paymentStatus });
                  reportFilterDialog.current?.showModal();
                }}
              >
                <ActionIcon action="filter" />
              </button>
              <button
                className="toolbar-icon-button"
                type="button"
                aria-label={refreshing ? "Refreshing finance" : "Refresh finance"}
                title="Refresh finance"
                disabled={refreshing}
                aria-busy={refreshing}
                onClick={async () => {
                  setRefreshing(true);
                  try {
                    await load();
                  } finally {
                    setRefreshing(false);
                  }
                }}
              >
                <ActionIcon action="refresh" />
              </button>
            </div>
          </div>
          <div className="operations-grid finance-report__metrics">
            <Card className="metric-card metric-card--income">
              <span>
                {role === "admin" && scope === "platform" ? "Fees recorded" : "Income recorded"}
              </span>
              <strong>{money(reportIncome)}</strong>
              <small>
                {reportEntries.filter((e) => entryDirection(e, role) === "income").length} records
              </small>
            </Card>
            <Card className="metric-card metric-card--expense">
              <span>Expenses</span>
              <strong>{money(reportExpenses)}</strong>
              <small>
                {reportEntries.filter((e) => entryDirection(e, role) === "expense").length} records
              </small>
            </Card>
            <Card className="metric-card metric-card--balance">
              <span>Recorded difference</span>
              <strong>{money(reportIncome - reportExpenses)}</strong>
              <small>{reportEntries.length} total transactions</small>
            </Card>
          </div>
          <p className="management-help">
            Totals follow the list filters. Booking values and cash collected for orders are
            excluded from income. Recorded difference is not profit.
          </p>
          {role === "admin" && (
            <div className="operations-grid finance-report__metrics">
              <Card className="metric-card">
                <span>Receipts against these fees</span>
                <strong>
                  {money(
                    reportEntries
                      .filter((e) =>
                        ["subscription_fee", "promotion_fee", "event_fee"].includes(e.category),
                      )
                      .reduce((n, e) => n + Number(e.received_minor), 0),
                  )}
                </strong>
              </Card>
              <Card className="metric-card">
                <span>Balance without receipts</span>
                <strong>
                  {money(
                    reportEntries
                      .filter((e) =>
                        ["subscription_fee", "promotion_fee", "event_fee"].includes(e.category),
                      )
                      .reduce((n, e) => n + Number(e.amount_minor) - Number(e.received_minor), 0),
                  )}
                </strong>
                <small>Existing fees are not assumed paid.</small>
              </Card>
            </div>
          )}
          <Card className="finance-report__breakdown">
            <div className="finance-report__breakdown-heading">
              <div>
                <h3>Category breakdown</h3>
              </div>
              <strong>{reportEntries.length} records</strong>
            </div>
            {categoryReport.length ? (
              <div className="finance-report__categories">
                {categoryReport.map((category) => {
                  const total = category.income + category.expenses;
                  const net = category.income - category.expenses;
                  return (
                    <div className="finance-report__category" key={category.category}>
                      <div>
                        <strong>{labels[category.category] ?? category.category}</strong>
                        <span>{category.count} transactions</span>
                      </div>
                      <span className="finance-report__bar" aria-hidden="true">
                        <i style={{ width: `${Math.max(4, (total / largestCategory) * 100)}%` }} />
                      </span>
                      <b className={net < 0 ? "finance-report__amount--expense" : ""}>
                        {net < 0 ? "−" : "+"}
                        {money(Math.abs(net))}
                      </b>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="notification-empty">No finance records in this period.</p>
            )}
          </Card>
        </section>
      )}
      <dialog
        ref={reportFilterDialog}
        className="customer-order-filter-dialog subscription-popup"
        aria-labelledby="finance-filter-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setScope(draftReport.scope);
            setReportPeriod(draftReport.reportPeriod);
            setDirection(draftReport.direction);
            setPaymentStatus(draftReport.paymentStatus);
            reportFilterDialog.current?.close();
          }}
        >
          <header>
            <h2 id="finance-filter-title">Filter finance</h2>
            <button
              type="button"
              className="icon-button"
              aria-label="Close finance filters"
              onClick={() => reportFilterDialog.current?.close()}
            >
              ×
            </button>
          </header>
          {role === "admin" && (
            <label className="field">
              Finance scope
              <select
                value={draftReport.scope}
                onChange={(event) => setDraftReport({ ...draftReport, scope: event.target.value })}
              >
                <option value="platform">Platform fees</option>
                <option value="provider">Provider finances</option>
                <option value="all">All organization records</option>
              </select>
            </label>
          )}
          <label className="field">
            Reporting period
            <select
              value={draftReport.reportPeriod}
              onChange={(event) =>
                setDraftReport({ ...draftReport, reportPeriod: event.target.value as ReportPeriod })
              }
            >
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
              <option value="all">All time</option>
            </select>
          </label>
          <label className="field">
            Receipt status
            <select
              value={draftReport.paymentStatus}
              onChange={(event) =>
                setDraftReport({ ...draftReport, paymentStatus: event.target.value })
              }
            >
              <option value="all">All statuses</option>
              {Object.entries(receiptLabels).map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            Type
            <select
              value={draftReport.direction}
              onChange={(event) =>
                setDraftReport({
                  ...draftReport,
                  direction: event.target.value as typeof direction,
                })
              }
            >
              <option value="all">All records</option>
              <option value="income">Income</option>
              <option value="expense">Expense</option>
            </select>
          </label>
          <div className="subscription-popup-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={() =>
                setDraftReport({
                  scope: role === "admin" ? "platform" : "all",
                  reportPeriod: "30",
                  direction: "all",
                  paymentStatus: "all",
                })
              }
            >
              Reset
            </button>
            <button type="submit" className="button">
              Apply filters
            </button>
          </div>
        </form>
      </dialog>
      {recordOpen && role !== "customer" && organizations.length > 0 && (
        <div
          className="finance-dialog-backdrop"
          role="presentation"
          onMouseDown={() => setRecordOpen(false)}
        >
          <section
            className="finance-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="finance-entry-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="finance-dialog__header">
              <div>
                <p className="eyebrow">New finance record</p>
                <h2 id="finance-entry-title">Record a cash movement</h2>
                <p>Entries are permanent and included in finance reports.</p>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label="Close"
                onClick={() => setRecordOpen(false)}
              >
                ×
              </button>
            </header>
            <form
              className="finance-record-grid finance-dialog__form"
              onSubmit={(e) => void record(e)}
            >
              <label className="field">
                <span>Owner</span>
                <select name="organization_id" required>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Type</span>
                <select name="direction" defaultValue="income">
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </label>
              <label className="field">
                <span>Amount (MMK)</span>
                <input name="amount" type="number" min="1" step="1" required />
              </label>
              <label className="field">
                <span>Date and time</span>
                <input
                  name="occurred_at"
                  type="datetime-local"
                  defaultValue={defaultOccurredAt}
                  required
                />
              </label>
              <label className="field finance-record-grid__description">
                <span>Description</span>
                <input name="description" minLength={2} maxLength={240} required />
              </label>
              <button className="button" disabled={busy}>
                {busy ? "Recording…" : "Add record"}
              </button>
            </form>
          </section>
        </div>
      )}
      <div className="finance-ledger">
        <div className="section-heading section-heading--compact">
          <div>
            <h2>{role === "customer" ? "My transaction history" : "Finance records"}</h2>
          </div>
        </div>
        <ManagementListToolbar
          query={query}
          onQueryChange={setQuery}
          searchLabel="Search finance records"
          resultCount={filtered.length}
          filters={
            role === "customer"
              ? [
                  {
                    label: "Receipt status",
                    value: paymentStatus,
                    onChange: setPaymentStatus,
                    options: [
                      { value: "all", label: "All statuses" },
                      ...Object.entries(receiptLabels).map(([value, label]) => ({ value, label })),
                    ],
                  },
                  {
                    label: "Type",
                    value: direction,
                    onChange: (value) => setDirection(value as typeof direction),
                    options: [
                      { value: "all", label: "All records" },
                      { value: "income", label: "Income" },
                      { value: "expense", label: "Expense" },
                    ],
                  },
                ]
              : []
          }
          onClear={() => {
            setQuery("");
            setDirection("all");
            setPaymentStatus("all");
          }}
        />
        <div className="filtered-list-stack">
          <PaginatedList
            items={filtered}
            resetKey={`${query}:${direction}:${paymentStatus}:${scope}:${reportPeriod}`}
            label="Finance record pages"
            renderItem={(entry) => {
              const effectiveDirection = entryDirection(entry, role);
              return (
                <Card className="ledger-row" key={entry.id}>
                  <div className={`ledger-row__icon ledger-row__icon--${effectiveDirection}`}>
                    {effectiveDirection === "income" ? "+" : "−"}
                  </div>
                  <div className="ledger-row__body">
                    <div>
                      <strong>{entry.description}</strong>
                      <span>{orgName(entry)}</span>
                    </div>
                    <div>
                      <strong>{money(entry.amount_minor, entry.currency)}</strong>
                      <span>{formatRecordDate(entry.occurred_at, "en", true)}</span>
                    </div>
                  </div>
                  <div className="fee-receipt-status">
                    <StatusPill tone={effectiveDirection === "income" ? "success" : "warning"}>
                      {labels[entry.category] ?? entry.category}
                    </StatusPill>
                    {receiptStatus(entry) !== "not_applicable" && (
                      <>
                        <StatusPill
                          tone={receiptStatus(entry) === "received" ? "success" : "warning"}
                        >
                          {receiptLabels[receiptStatus(entry)]}
                        </StatusPill>
                        <small>Received: {money(Number(entry.received_minor))}</small>
                        {role === "admin" && receiptStatus(entry) !== "received" && (
                          <button
                            type="button"
                            className="button button--secondary"
                            onClick={() => setReceiptEntry(entry)}
                          >
                            Record payment received
                          </button>
                        )}
                        {entry.receipts.length > 0 && (
                          <details>
                            <summary>Receipt history</summary>
                            {entry.receipts.map((receipt, i) => (
                              <p key={i}>
                                {receipt.received_on} · {money(Number(receipt.amount_minor))} ·{" "}
                                {receipt.reference}
                              </p>
                            ))}
                          </details>
                        )}
                      </>
                    )}
                  </div>
                </Card>
              );
            }}
          />
          {!filtered.length && (
            <Card className="empty-state">
              <span className="empty-state__icon">₭</span>
              <strong>No finance records match these filters.</strong>
              <p>New completed services or recorded cash movements will appear here.</p>
            </Card>
          )}
        </div>
      </div>
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

interface SubscriptionRow extends Subscription {
  organization: Organization;
  configured: boolean;
}
const subscriptionStatuses = [
  "all",
  "active",
  "trial",
  "past_due",
  "suspended",
  "cancelled",
  "not_set",
];
const subscriptionLabel = (value: string) =>
  value === "all"
    ? "All subscriptions"
    : value.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
const subscriptionStatus = (row: SubscriptionRow) => (row.configured ? row.status : "not_set");
export function AdminSubscriptionManagement({ showHeading = true }: { showHeading?: boolean }) {
  const auth = useAuth();
  const [rows, setRows] = useState<SubscriptionRow[]>([]);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState({ status: "all", kind: "all", plan: "all", cycle: "all" });
  const [draft, setDraft] = useState(filters);
  const [draftQuery, setDraftQuery] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<SubscriptionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useFeedback();
  const [detailMessage, setDetailMessage] = useFeedback();
  const [busyId, setBusyId] = useState("");
  const filterDialog = useRef<HTMLDialogElement>(null);
  const detailDialog = useRef<HTMLDialogElement>(null);
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    setLoading(true);
    try {
      const [orgs, subs] = await Promise.all([
        auth.client.from("organizations").select("id,name,kind").order("name"),
        auth.client
          .from("organization_subscriptions")
          .select(
            "id,organization_id,plan,status,billing_cycle,fee_minor,currency,starts_at,renews_at,updated_at",
          ),
      ]);
      const error = orgs.error ?? subs.error;
      if (error) {
        setMessage(error.message);
        return;
      }
      const byOrg = new Map(
        ((subs.data as Subscription[] | null) ?? []).map((s) => [s.organization_id, s]),
      );
      setRows(
        ((orgs.data as Organization[] | null) ?? []).map((organization) => ({
          ...(byOrg.get(organization.id) ?? {
            id: organization.id,
            organization_id: organization.id,
            plan: "starter" as const,
            status: "trial" as const,
            billing_cycle: "monthly" as const,
            fee_minor: 0,
            currency: "MMK",
            starts_at: new Date().toISOString(),
            renews_at: null,
            updated_at: new Date().toISOString(),
          }),
          organization,
          configured: byOrg.has(organization.id),
        })),
      );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Could not load subscriptions.");
    } finally {
      setLoading(false);
    }
  }, [auth.client, auth.user, setMessage]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (filters.status === "all" || subscriptionStatus(row) === filters.status) &&
        (filters.kind === "all" || row.organization.kind === filters.kind) &&
        (filters.plan === "all" || (row.configured && row.plan === filters.plan)) &&
        (filters.cycle === "all" || (row.configured && row.billing_cycle === filters.cycle)) &&
        `${row.organization.name} ${row.organization.kind}`.toLowerCase().includes(needle),
    );
  }, [query, rows, filters]);
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(filtered.length / RECORDS_PER_PAGE) - 1),
  );
  const activeFilters = Object.values(filters).filter((value) => value !== "all").length;
  async function save(row: SubscriptionRow, form: HTMLFormElement) {
    if (!auth.client || busyId) return;
    const data = new FormData(form);
    const fee = Math.round(Number(data.get("fee")) * 100);
    if (!Number.isSafeInteger(fee) || fee < 0) {
      setDetailMessage("Enter a valid fee amount.");
      return;
    }
    setBusyId(row.organization_id);
    setDetailMessage("");
    try {
      const { error } = await auth.client.rpc("admin_upsert_organization_subscription", {
        target_organization_id: row.organization_id,
        target_plan: String(data.get("plan")),
        target_status: String(data.get("status")),
        target_billing_cycle: String(data.get("billing_cycle")),
        target_fee_minor: fee,
        target_renews_at: String(data.get("renews_at")) || null,
      });
      if (error) throw new Error(error.message);
      setMessage(`${row.organization.name} subscription was updated and audited.`);
      detailDialog.current?.close();
      await load();
    } catch (error) {
      setDetailMessage(error instanceof Error ? error.message : "Unable to save subscription.");
    } finally {
      setBusyId("");
    }
  }
  return (
    <section className="subscription-management">
      {showHeading && (
        <div className="section-heading">
          <div>
            <h2>Subscriptions</h2>
          </div>
        </div>
      )}

      <div className="subscription-table-toolbar">
        <button
          type="button"
          className="toolbar-icon-button"
          aria-label="Filter subscriptions"
          title="Filter subscriptions"
          aria-haspopup="dialog"
          data-filtered={activeFilters > 0}
          onClick={() => {
            setDraftQuery(query);
            setDraft(filters);
            filterDialog.current?.showModal();
          }}
        >
          <ActionIcon action="filter" />
        </button>
        <button
          type="button"
          className="toolbar-icon-button"
          aria-label="Refresh subscriptions"
          title="Refresh subscriptions"
          disabled={loading}
          onClick={() => void load()}
        >
          <ActionIcon action="refresh" />
        </button>
      </div>
      <div className="subscription-filter-summary" aria-live="polite">
        <span>
          {filtered.length} {filtered.length === 1 ? "subscription" : "subscriptions"}
          {activeFilters
            ? ` · ${Object.values(filters)
                .filter((value) => value !== "all")
                .map(subscriptionLabel)
                .join(" · ")}`
            : ""}
        </span>
        {(query || activeFilters > 0) && (
          <button
            type="button"
            onClick={() => {
              setFilters({ status: "all", kind: "all", plan: "all", cycle: "all" });
              setQuery("");
              setPage(0);
            }}
          >
            Clear filters
          </button>
        )}
      </div>
      {message && <p role="status">{message}</p>}
      <div
        className="subscription-table-scroll"
        role="region"
        aria-label="Subscriptions table"
        tabIndex={0}
      >
        <table className="subscription-table">
          <caption className="visually-hidden">
            Provider subscriptions, five records per page
          </caption>
          <thead>
            <tr>
              <th scope="col">Provider</th>
              <th scope="col">Status</th>
              <th scope="col">Plan</th>
              <th scope="col">Fee / cycle</th>
              <th scope="col">Renews</th>
              <th scope="col">Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, (currentPage + 1) * RECORDS_PER_PAGE).map((row) => (
              <tr key={row.organization_id}>
                <th scope="row">
                  <strong>{row.organization.name}</strong>
                  <small>{subscriptionLabel(row.organization.kind ?? "service")}</small>
                </th>
                <td>
                  <StatusPill
                    tone={row.configured && row.status === "active" ? "success" : "warning"}
                  >
                    {subscriptionLabel(subscriptionStatus(row))}
                  </StatusPill>
                </td>
                <td>{row.configured ? subscriptionLabel(row.plan) : "—"}</td>
                <td>
                  {row.configured ? (
                    <>
                      {money(row.fee_minor, row.currency)}
                      <small>{subscriptionLabel(row.billing_cycle)}</small>
                    </>
                  ) : (
                    "—"
                  )}
                </td>
                <td>{row.renews_at?.slice(0, 10) ?? "Not scheduled"}</td>
                <td>
                  <button
                    type="button"
                    className="button button--secondary"
                    aria-label={`View ${row.organization.name} subscription`}
                    onClick={() => {
                      setSelected(row);
                      setDetailMessage("");
                      detailDialog.current?.showModal();
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td colSpan={6}>
                  {loading ? "Loading subscriptions…" : "No subscriptions match these filters."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <ServerPagination
        page={currentPage}
        total={filtered.length}
        onPage={setPage}
        loading={loading}
      />
      <dialog
        ref={filterDialog}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.currentTarget.close();
          }
        }}
        className="customer-order-filter-dialog subscription-popup"
        aria-labelledby="subscription-filter-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setQuery(draftQuery);
            setFilters(draft);
            setPage(0);
            filterDialog.current?.close();
          }}
        >
          <header>
            <h2 id="subscription-filter-title">Filter subscriptions</h2>
            <button
              type="button"
              className="button button--secondary"
              aria-label="Close filters"
              onClick={() => filterDialog.current?.close()}
            >
              ×
            </button>
          </header>
          <label className="field">
            <span className="visually-hidden">Search subscriptions</span>
            <input
              type="search"
              placeholder="Search providers"
              value={draftQuery}
              onChange={(event) => {
                setDraftQuery(event.target.value);
              }}
            />
          </label>
          {(
            [
              ["status", "Status", subscriptionStatuses],
              [
                "kind",
                "Service type",
                [
                  "all",
                  ...Array.from(
                    new Set(
                      rows
                        .map((row) => row.organization.kind)
                        .filter((kind): kind is string => Boolean(kind)),
                    ),
                  ),
                ],
              ],
              ["plan", "Plan", ["all", "starter", "growth", "enterprise"]],
              ["cycle", "Billing cycle", ["all", "monthly", "annual"]],
            ] as const
          ).map(([key, label, options]) => (
            <label className="field" key={key}>
              <span>{label}</span>
              <select
                aria-label={label}
                value={draft[key]}
                onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
              >
                {options.map((value) => (
                  <option key={value} value={value}>
                    {value === "all" ? "All" : subscriptionLabel(value)}
                  </option>
                ))}
              </select>
            </label>
          ))}
          <div className="subscription-popup-actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={() => setDraft({ status: "all", kind: "all", plan: "all", cycle: "all" })}
            >
              Reset
            </button>
            <button className="button button--primary">Apply filters</button>
          </div>
        </form>
      </dialog>
      <dialog
        ref={detailDialog}
        className="customer-order-filter-dialog subscription-popup"
        aria-labelledby="subscription-detail-title"
        onCancel={(event) => {
          if (busyId) event.preventDefault();
        }}
      >
        {selected && (
          <form
            key={`${selected.organization_id}:${selected.updated_at}`}
            onSubmit={(event) => {
              event.preventDefault();
              void save(selected, event.currentTarget);
            }}
          >
            <header>
              <h2 id="subscription-detail-title">Subscription details</h2>
              <button
                type="button"
                className="button button--secondary"
                aria-label="Close details"
                disabled={Boolean(busyId)}
                onClick={() => detailDialog.current?.close()}
              >
                ×
              </button>
            </header>
            <div>
              <h3>{selected.organization.name}</h3>
              <p>
                {subscriptionLabel(selected.organization.kind ?? "service")} ·{" "}
                {subscriptionLabel(subscriptionStatus(selected))}
              </p>
              <small>
                {selected.configured
                  ? `Last updated ${formatRecordDate(selected.updated_at, "en", true)}`
                  : "This provider has no subscription configured yet."}
              </small>
            </div>
            <fieldset disabled={Boolean(busyId)} className="subscription-detail-fields">
              <legend className="visually-hidden">Subscription settings</legend>
              <label className="field">
                <span>Plan</span>
                <select name="plan" defaultValue={selected.plan}>
                  {["starter", "growth", "enterprise"].map((value) => (
                    <option key={value} value={value}>
                      {subscriptionLabel(value)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Status</span>
                <select name="status" defaultValue={selected.status}>
                  {subscriptionStatuses
                    .filter((value) => value !== "all" && value !== "not_set")
                    .map((value) => (
                      <option key={value} value={value}>
                        {subscriptionLabel(value)}
                      </option>
                    ))}
                </select>
              </label>
              <label className="field">
                <span>Billing cycle</span>
                <select name="billing_cycle" defaultValue={selected.billing_cycle}>
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </select>
              </label>
              <label className="field">
                <span>Fee (MMK)</span>
                <input
                  name="fee"
                  type="number"
                  min="0"
                  max="1000000000"
                  step="0.01"
                  required
                  defaultValue={selected.fee_minor / 100}
                />
              </label>
              <label className="field">
                <span>Renewal date</span>
                <input
                  name="renews_at"
                  type="date"
                  defaultValue={selected.renews_at?.slice(0, 10) ?? ""}
                />
              </label>
            </fieldset>
            <p>
              Saving an active subscription with a fee creates a charge in Finance. Verify payments
              in <a href="/more/subscription-requests">Subscription requests</a>.
            </p>
            {detailMessage && <p role="alert">{detailMessage}</p>}
            <div className="subscription-popup-actions">
              <button
                type="button"
                className="button button--secondary"
                disabled={Boolean(busyId)}
                onClick={() => detailDialog.current?.close()}
              >
                Cancel
              </button>
              <button className="button button--primary" disabled={Boolean(busyId)}>
                {busyId ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </section>
  );
}

type PromotionProviderKind = "hotel" | "merchant" | "delivery" | "taxi";
type PromotionStatus = "pending" | "approved" | "rejected" | "cancelled";
interface PromotionResource {
  id: string;
  display_name: string;
}
interface PromotionRequest {
  id: string;
  event_id: string | null;
  organization_id: string;
  provider_kind: PromotionProviderKind;
  resource_id: string;
  resource_name: string;
  title: string;
  description: string;
  terms: string;
  starts_on: string;
  ends_on: string;
  status: PromotionStatus;
  charge_minor: number | string;
  display_priority: number;
  admin_note: string;
  created_at: string;
}

const promotionResourceConfig: Record<
  PromotionProviderKind,
  { readonly table: string; readonly label: string }
> = {
  hotel: { table: "hotel_properties", label: "hotel" },
  merchant: { table: "merchant_shops", label: "shop" },
  delivery: { table: "delivery_fleets", label: "delivery service" },
  taxi: { table: "taxi_groups", label: "taxi service" },
};

const promotionTone = (status: PromotionStatus) =>
  status === "approved" ? "success" : status === "pending" ? "warning" : "neutral";

const promotionDate = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(date);
};

const promotionDateRange = (startsOn: string, endsOn: string) =>
  `${promotionDate(startsOn)} – ${promotionDate(endsOn)}`;

const defaultPromotionDate = (offsetDays: number) => {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
};

export function PartnerPromotionRequests({
  providerKind,
}: {
  readonly providerKind: PromotionProviderKind;
}) {
  const auth = useAuth();
  const [resources, setResources] = useState<PromotionResource[]>([]);
  const [requests, setRequests] = useState<PromotionRequest[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [message, setMessage] = useFeedback();
  const [busy, setBusy] = useState(false);
  const [packages, setPackages] = useState<AdPackage[]>([]);
  useEffect(() => {
    if (!auth.client) return;
    void auth.client
      .from("ad_packages")
      .select("*")
      .eq("active", true)
      .then((r) => setPackages((r.data ?? []) as AdPackage[]));
  }, [auth.client]);
  const config = promotionResourceConfig[providerKind];

  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const membership = await auth.client
      .from("organization_memberships")
      .select("organization_id")
      .eq("user_id", auth.user.id)
      .eq("status", "active")
      .eq(
        "role",
        {
          hotel: "hotel_owner",
          merchant: "merchant_owner",
          delivery: "delivery_owner",
          taxi: "taxi_group_leader",
        }[providerKind],
      );
    if (membership.error) {
      setMessage(membership.error.message);
      return;
    }
    const organizationIds = ((membership.data ?? []) as { organization_id: string }[]).map(
      (m) => m.organization_id,
    );
    if (!organizationIds.length) {
      setResources([]);
      setRequests([]);
      return;
    }
    const [resourceResult, requestResult] = await Promise.all([
      auth.client
        .from(config.table)
        .select("id,display_name")
        .in("organization_id", organizationIds)
        .order("display_name"),
      auth.client
        .from("special_promotion_requests")
        .select(
          "id,event_id,organization_id,provider_kind,resource_id,resource_name,title,description,terms,starts_on,ends_on,status,charge_minor,display_priority,admin_note,created_at",
        )
        .eq("provider_kind", providerKind)
        .in("organization_id", organizationIds)
        .order("created_at", { ascending: false }),
    ]);
    const error = resourceResult.error ?? requestResult.error;
    if (error) {
      setMessage(error.message);
      return;
    }
    setResources((resourceResult.data as PromotionResource[] | null) ?? []);
    setRequests(
      ((requestResult.data as PromotionRequest[] | null) ?? []).filter((r) => !r.event_id),
    );
  }, [auth.client, auth.user, config.table, providerKind, setMessage]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!auth.client || busy) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setBusy(true);
    setMessage("");
    const packageId = String(form.get("package") ?? "");
    const { error } = await auth.client.rpc(
      packageId ? "request_ad_package" : "request_special_promotion",
      packageId
        ? {
            package: packageId,
            provider: providerKind,
            resource: String(form.get("resource_id")),
            title: String(form.get("title")),
            description: String(form.get("description")),
            terms: String(form.get("terms")),
            starts: String(form.get("starts_on")),
          }
        : {
            target_provider_kind: providerKind,
            target_resource_id: String(form.get("resource_id")),
            promotion_title: String(form.get("title")),
            promotion_description: String(form.get("description")),
            promotion_terms: String(form.get("terms")),
            target_starts_on: String(form.get("starts_on")),
            target_ends_on: String(form.get("ends_on")),
          },
    );
    setBusy(false);
    if (error) {
      setMessage(error.message);
      return;
    }
    formElement.reset();
    setFormOpen(false);
    setMessage("Promotion request sent to admin for review.");
    await load();
  }

  const filteredRequests = requests.filter(
    (r) =>
      (status === "all" || r.status === status) &&
      `${r.title} ${r.resource_name}`.toLowerCase().includes(query.trim().toLowerCase()),
  );
  if (!auth.user)
    return (
      <Card className="empty-state">
        <strong>Sign in to request a promotion.</strong>
      </Card>
    );

  return (
    <section className="promotion-management" aria-labelledby={`${providerKind}-promotion-title`}>
      <AdPackages />
      <div className="section-heading">
        <div>
          <h2 id={`${providerKind}-promotion-title`}>Request a special promotion</h2>
        </div>
      </div>
      {!resources.length && (
        <Card>
          <p>Create and activate your {config.label} before requesting a promotion.</p>
        </Card>
      )}
      <ManagementListToolbar
        actions={
          resources.length > 0 && (
            <button
              className="toolbar-icon-button"
              type="button"
              aria-label="Create promotion request"
              title="Create promotion request"
              aria-haspopup="dialog"
              onClick={() => setFormOpen(true)}
            >
              <ActionIcon action="add" />
            </button>
          )
        }
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search promotion requests"
        resultCount={filteredRequests.length}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: ["all", "pending", "approved", "rejected", "cancelled"].map((value) => ({
              value,
              label: value === "all" ? "All statuses" : value,
            })),
          },
        ]}
        onClear={() => {
          setQuery("");
          setStatus("all");
        }}
      />
      <div className="filtered-list-stack">
        <PaginatedList
          items={filteredRequests}
          resetKey={`${query}:${status}`}
          label="Promotion request pages"
          renderItem={(request) => (
            <Card className="audit-row" key={request.id}>
              <div>
                <StatusPill tone={promotionTone(request.status)}>{request.status}</StatusPill>
                <h3>{request.title}</h3>
                <p>
                  {request.resource_name} · {promotionDateRange(request.starts_on, request.ends_on)}
                </p>
                {request.admin_note && <small>Admin: {request.admin_note}</small>}
              </div>
              <div className="audit-row__meta">
                <strong>
                  {request.event_id
                    ? "Included in festival fee"
                    : money(Number(request.charge_minor))}
                </strong>
                <span>
                  {request.event_id
                    ? "Requires approved, paid participation"
                    : request.status === "approved"
                      ? "Approved charge"
                      : "Charge set on approval"}
                </span>
              </div>
            </Card>
          )}
        />
        {!filteredRequests.length && (
          <Card className="empty-state">
            <strong>No promotion requests match these filters.</strong>
          </Card>
        )}
      </div>
      {formOpen && resources.length > 0 && (
        <div
          className="finance-dialog-backdrop"
          role="presentation"
          onMouseDown={() => setFormOpen(false)}
        >
          <section
            className="finance-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${providerKind}-promotion-form-title`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="finance-dialog__header">
              <div>
                <p className="eyebrow">Paid visibility</p>
                <h2 id={`${providerKind}-promotion-form-title`}>Create promotion request</h2>
              </div>
              <button
                className="icon-button"
                type="button"
                aria-label="Cancel"
                onClick={() => setFormOpen(false)}
              >
                ×
              </button>
            </header>
            <form
              className="form-grid form-grid--two finance-dialog__form"
              onSubmit={(event) => void submit(event)}
            >
              <label className="field">
                <span>{config.label.charAt(0).toUpperCase() + config.label.slice(1)}</span>
                <select name="resource_id" required>
                  {resources.map((resource) => (
                    <option value={resource.id} key={resource.id}>
                      {resource.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <a href="/more/provider-events">Manage festival prices inside Events</a>
              <label className="field">
                <span>Advertising package</span>
                <select name="package">
                  <option value="">Custom quote from admin</option>
                  {packages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.duration_days} days · {p.fee_minor / 100} MMK
                    </option>
                  ))}
                </select>
                <small>
                  A package sets the duration automatically; the end date below applies to custom
                  quotes.
                </small>
              </label>
              <label className="field">
                <span>Offer title</span>
                <input name="title" minLength={3} maxLength={120} required />
              </label>
              <label className="field">
                <span>Starts</span>
                <input
                  name="starts_on"
                  type="date"
                  defaultValue={defaultPromotionDate(1)}
                  required
                />
              </label>
              <label className="field">
                <span>Ends</span>
                <input
                  name="ends_on"
                  type="date"
                  defaultValue={defaultPromotionDate(31)}
                  required
                />
              </label>
              <label className="field field--wide">
                <span>Customer offer</span>
                <textarea name="description" minLength={10} maxLength={600} rows={4} required />
              </label>
              <label className="field field--wide">
                <span>Terms (optional)</span>
                <textarea name="terms" maxLength={500} rows={3} />
              </label>
              <div className="button-row field--wide">
                <button className="button" disabled={busy}>
                  {busy ? "Sending…" : "Send promotion request"}
                </button>
                <button
                  className="button button--secondary"
                  type="button"
                  onClick={() => setFormOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

export function AdminPromotionReview() {
  const auth = useAuth();
  const [requests, setRequests] = useState<PromotionRequest[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | PromotionStatus>("all");
  const [provider, setProvider] = useState<"all" | PromotionProviderKind>("all");
  const [message, setMessage] = useFeedback();
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const { data, error } = await auth.client
      .from("special_promotion_requests")
      .select(
        "id,event_id,organization_id,provider_kind,resource_id,resource_name,title,description,terms,starts_on,ends_on,status,charge_minor,display_priority,admin_note,created_at",
      )
      .order("created_at", { ascending: false });
    if (error) setMessage(error.message);
    else setRequests(((data as PromotionRequest[] | null) ?? []).filter((r) => !r.event_id));
  }, [auth.client, auth.user, setMessage]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  async function review(formElement: HTMLFormElement, decision: "approved" | "rejected") {
    if (!auth.client) return;
    const form = new FormData(formElement);
    const id = String(form.get("request_id"));
    setBusyId(id);
    setMessage("");
    const { error } = await auth.client.rpc("review_special_promotion", {
      target_request_id: id,
      target_decision: decision,
      target_charge_minor:
        decision === "approved" ? Math.round(Number(form.get("charge")) * 100) : 0,
      target_display_priority: decision === "approved" ? Number(form.get("priority")) : 0,
      target_admin_note: String(form.get("admin_note")),
    });
    setBusyId("");
    if (error) {
      setMessage(error.message);
      return;
    }
    setMessage(
      decision === "approved"
        ? "Promotion approved. It becomes visible after the required payment is recorded."
        : "Promotion rejected.",
    );
    await load();
  }

  const filteredRequests = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return requests
      .filter(
        (request) =>
          (status === "all" || request.status === status) &&
          (provider === "all" || request.provider_kind === provider) &&
          (!normalized ||
            [request.title, request.resource_name, request.description].some((value) =>
              value.toLocaleLowerCase().includes(normalized),
            )),
      )
      .sort((a, b) =>
        a.status === b.status
          ? b.created_at.localeCompare(a.created_at)
          : a.status === "pending"
            ? -1
            : 1,
      );
  }, [provider, query, requests, status]);
  return (
    <section className="promotion-management" aria-label="Special promotion requests">
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search promotion or provider"
        resultCount={filteredRequests.length}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: (value) => setStatus(value as "all" | PromotionStatus),
            options: [
              { value: "all", label: "All statuses" },
              { value: "pending", label: "Pending" },
              { value: "approved", label: "Approved" },
              { value: "rejected", label: "Rejected" },
              { value: "cancelled", label: "Cancelled" },
            ],
          },
          {
            label: "Service",
            value: provider,
            onChange: (value) => setProvider(value as "all" | PromotionProviderKind),
            options: [
              { value: "all", label: "All services" },
              { value: "hotel", label: "Link Stay" },
              { value: "merchant", label: "Link Food" },
              { value: "delivery", label: "Link Delivery" },
              { value: "taxi", label: "Link Ride" },
            ],
          },
        ]}
        onClear={() => {
          setQuery("");
          setStatus("all");
          setProvider("all");
        }}
      />
      <div className="filtered-list-stack">
        <PaginatedList
          items={filteredRequests}
          resetKey={`${query}:${status}:${provider}`}
          label="Promotion approval pages"
          renderItem={(request) => (
            <DetailCard
              title={request.title}
              summary={
                <StatusPill tone={promotionTone(request.status)}>{request.status}</StatusPill>
              }
              className="promotion-review-card"
              key={request.id}
            >
              <div className="section-heading">
                <div>
                  <StatusPill tone={promotionTone(request.status)}>{request.status}</StatusPill>
                  <h3>{request.title}</h3>
                </div>
                <strong>
                  {request.event_id
                    ? "Included in festival participation fee"
                    : money(Number(request.charge_minor))}
                </strong>
              </div>
              <p>{request.description}</p>
              {request.terms && <small>Terms: {request.terms}</small>}
              {request.status === "pending" ? (
                <form
                  className="form-grid form-grid--two"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void review(event.currentTarget, "approved");
                  }}
                >
                  <input name="request_id" type="hidden" value={request.id} />
                  <label className="field">
                    <span>Charge (MMK)</span>
                    <input
                      name="charge"
                      type="number"
                      min={request.event_id ? "0" : "1"}
                      step="1"
                      required
                      defaultValue={request.event_id ? "0" : undefined}
                      readOnly={!!request.event_id}
                    />
                  </label>
                  <label className="field">
                    <span>Display priority (0–100)</span>
                    <input
                      name="priority"
                      type="number"
                      min="0"
                      max="100"
                      defaultValue="50"
                      required
                    />
                  </label>
                  <label className="field field--wide">
                    <span>Admin note</span>
                    <textarea name="admin_note" maxLength={500} rows={2} />
                  </label>
                  <div className="button-row promotion-review-actions">
                    <button
                      className="action-capsule action-capsule--enable"
                      disabled={busyId === request.id}
                    >
                      {busyId === request.id ? "Saving…" : "Approve promotion"}
                    </button>
                    <button
                      className="action-capsule action-capsule--disable"
                      type="submit"
                      formNoValidate
                      disabled={busyId === request.id}
                      onClick={(event) => {
                        event.preventDefault();
                        void review(event.currentTarget.form!, "rejected");
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </form>
              ) : (
                request.admin_note && <p>Admin note: {request.admin_note}</p>
              )}
            </DetailCard>
          )}
        />
        {!filteredRequests.length && (
          <Card className="empty-state">
            <strong>No promotion requests match these filters.</strong>
          </Card>
        )}
      </div>
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

interface Audit {
  id: number;
  action: string;
  resource_type: string;
  organization_name: string;
  actor_name: string;
  actor_email: string;
  occurred_at: string;
}
export function AdminAuditTrail() {
  const auth = useAuth();
  const [records, setRecords] = useState<Audit[]>([]);
  const [query, setQuery] = useState("");
  const [resource, setResource] = useState("all");
  const [message, setMessage] = useFeedback();
  useEffect(() => {
    if (!auth.client || !auth.user) return;
    void auth.client.rpc("admin_audit_records", { target_limit: 250 }).then(({ data, error }) => {
      if (error) setMessage(error.message);
      else setRecords((data as Audit[] | null) ?? []);
    });
  }, [auth.client, auth.user, setMessage]);
  const resources = Array.from(new Set(records.map((record) => record.resource_type))).sort();
  const filtered = records.filter((record) => {
    const needle = query.trim().toLowerCase();
    return (
      (resource === "all" || record.resource_type === resource) &&
      (!needle ||
        `${record.action} ${record.organization_name} ${record.actor_email}`
          .toLowerCase()
          .includes(needle))
    );
  });
  return (
    <section className="audit-trail">
      <ManagementListToolbar
        query={query}
        onQueryChange={setQuery}
        searchLabel="Search audit records"
        resultCount={filtered.length}
        filters={[
          {
            label: "Resource",
            value: resource,
            onChange: setResource,
            options: [
              { value: "all", label: "All resources" },
              ...resources.map((value) => ({ value, label: value.replaceAll("_", " ") })),
            ],
          },
        ]}
        onClear={() => {
          setQuery("");
          setResource("all");
        }}
      />
      <div className="audit-list filtered-list-stack">
        <PaginatedList
          items={filtered}
          resetKey={`${query}:${resource}`}
          label="Audit record pages"
          renderItem={(record) => (
            <DetailCard
              title={record.action.replaceAll(".", " · ")}
              summary={
                <p>
                  {record.organization_name} · {formatRecordDate(record.occurred_at, "en", true)}
                </p>
              }
              className="audit-row"
              key={record.id}
            >
              <div>
                <StatusPill>{record.resource_type.replaceAll("_", " ")}</StatusPill>
                <h3>{record.action.replaceAll(".", " · ")}</h3>
                <p>{record.organization_name}</p>
              </div>
              <div className="audit-row__meta">
                <strong>{record.actor_name || record.actor_email || "System"}</strong>
                {record.actor_name && record.actor_email && <span>{record.actor_email}</span>}
                <time dateTime={record.occurred_at}>
                  {formatRecordDate(record.occurred_at, "en", true)}
                </time>
              </div>
            </DetailCard>
          )}
        />
        {!filtered.length && (
          <Card className="empty-state">
            <strong>No audit records match these filters.</strong>
          </Card>
        )}
      </div>
      {message && <p className="form-message">{message}</p>}
    </section>
  );
}

export { TaxiSubscriptionRequests } from "./taxi-subscriptions";

export { EventsWorkspace } from "./events";

export { ProviderSettings } from "./provider-settings";

export { ServiceCare } from "./service-care";

export { AdPackages } from "./ad-packages";

export { AdminTeam, StaffWorkspace } from "./admin-team";

export { ProviderReputation, ProviderSaveButton } from "./provider-reputation";

export * from "./workspace-design";
